import React, { useState, useMemo, useEffect } from 'react';
import { 
  ShoppingCart, X, Plus, Minus, Send, Star, Gift, Heart, Package, Instagram, Facebook, MessageCircle, Youtube, Mail, ChevronLeft, ChevronRight, Scale
} from 'lucide-react';

// Product Data with Media Arrays
const PRODUCTS = [
  { 
    id: 1, 
    name: { en: "Premium Executive Diary & Pen Gift Set", bn: "প্রিমিয়াম এক্সিকিউটিভ ডায়েরি এবং কলম উপহার সেট" }, 
    price: 499, 
    category: "Self Care", 
    rating: 4.9, 
    media: [
      { type: 'image', url: "/watch3.png" },
      { type: 'image', url: "/watch2.png" },
      { type: 'image', url: "/watch1.png" },
    ],
    desc: { en: "Perfect for office, personal use, or gifting this Premium Executive Diary & Pen Gift Set fits every occasion.", bn: "অফিস, ব্যক্তিগত ব্যবহার বা উপহার সব ক্ষেত্রেই এই প্রিমিয়াম এক্সিকিউটিভ ডায়েরি ও পেন গিফট সেট একদম মানানসই।" } 
  },
  { 
    id: 2, 
    name: { en: "Soft Heart Love Cushion", bn: "নরম হৃদয় প্রেমের কুশন" }, 
    price: 149,
    category: "Men", 
    rating: 4.8, 
    media: [
      { type: 'image', url: "/love2.jpeg" },
      { type: 'image', url: "/love1.jpeg" },
      { type: 'image', url: "/love3.jpeg" },
    ],
    desc: { en: "A small but special gift to express love this heart shape soft cushion.", bn: "ভালোবাসা প্রকাশের জন্য একটি ছোট কিন্তু বিশেষ উপহার এই হৃদয় আকৃতির নরম কুশন।" } 
  },
  { 
    id: 3, 
    name: { en: "Men’s Premium Gift Combo Box.", bn: "পুরুষদের প্রিমিয়াম গিফট কম্বো বক্স।" }, 
    price: 599, 
    category: "Food & Drink", 
    rating: 4.7, 
    media: [
      { type: 'image', url: "/abd.jpeg" },
      { type: 'image', url: "/3_gifts.png" },
      { type: 'image', url: "/asd.jpeg" },
      { type: 'image', url: "/asdf.jpeg" },
    ],
    desc: { en: "The perfect combination of style and utility—this Men’s Premium Gift Combo Box.", bn: "স্টাইল এবং ইউটিলিটির নিখুঁত সংমিশ্রণ—এই পুরুষদের প্রিমিয়াম গিফট কম্বো বক্স।" } 
  },
];

const CATEGORIES = [
  { id: "All", en: "All", bn: "সবগুলো" },
  { id: "Self Care", en: "Self Care", bn: "সেলফ কেয়ার" },
  { id: "Men", en: "Men", bn: "পুরুষদের জন্য" },
  { id: "Food & Drink", en: "Food & Drink", bn: "খাবার ও পানীয়" },
  { id: "Baby", en: "Baby", bn: "বাচ্চাদের জন্য" },
  { id: "Wedding", en: "Wedding", bn: "বিয়ে" }
];

// Media Slider Component
const MediaSlider = ({ media, name }) => {
  const [index, setIndex] = useState(0);

  const handleNext = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev + 1) % media.length);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  const current = media[index];

  return (
    <div className="relative w-full h-full group/slider">
      {current.type === 'image' ? (
        <img 
          src={current.url} 
          alt={name} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
        />
      ) : (
        <video 
          src={current.url} 
          className="w-full h-full object-cover" 
          autoPlay 
          muted 
          loop 
          playsInline 
        />
      )}

      <button 
        onClick={handlePrev}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 p-1.5 rounded-full shadow-md opacity-0 group-hover/slider:opacity-100 transition-opacity hover:bg-amber-600 hover:text-white"
      >
        <ChevronLeft size={20} />
      </button>
      <button 
        onClick={handleNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 p-1.5 rounded-full shadow-md opacity-0 group-hover/slider:opacity-100 transition-opacity hover:bg-amber-600 hover:text-white"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {media.map((_, i) => (
          <div 
            key={i} 
            className={`h-1 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-amber-600' : 'w-2 bg-white/60'}`} 
          />
        ))}
      </div>
    </div>
  );
};

const App = () => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false); // T&C Modal State
  const [filter, setFilter] = useState("All");
  const [checkoutStatus, setCheckoutStatus] = useState(null); 
  const [lang, setLang] = useState('en'); 
  const [showLangPopup, setShowLangPopup] = useState(false);

  const t = {
    en: {
      home: "Home", shop: "Shop", about: "About", contact: "Contact",
      hero_title: "Gifts That Tell A Story.", hero_sub: "Exquisitely Curated",
      cta: "DISCOVER COLLECTION", catalog: "The Catalog", add: "QUICK ADD",
      cart_title: "Your Selection", subtotal: "Subtotal", whatsapp_btn: "ORDER VIA WHATSAPP",
      lang_q: "Would you like to view the website in Bengali?", yes: "Yes, Bengali",
      no: "No, English", empty: "Your collection is empty.", about_title: "About kundagiftes",
      about_p: "We believe that the best gifts are more than just items in a box; they are shared moments and lasting memories.",
      personalized: "Personalized", premium: "Premium", personalized_sub: "Handwritten notes in every box.",
      premium_sub: "Highest grade artisanal goods.", connect: "Let's Connect",
      connect_sub: "Connect via WhatsApp or Email for custom inquiries.", newsletter: "Join the Newsletter",
      rights: "All Rights Reserved.", terms: "Terms & Conditions"
    },
    bn: {
      home: "হোম", shop: "শপ", about: "আমাদের সম্পর্কে", contact: "যোগাযোগ",
      hero_title: "উপহার যা গল্প বলে।", hero_sub: "নিখুঁতভাবে কিউরেটেড",
      cta: "কালেকশন দেখুন", catalog: "ক্যাটালগ", add: "যুক্ত করুন",
      cart_title: "আপনার নির্বাচন", subtotal: "মোট মূল্য", whatsapp_btn: "হোয়াটসঅ্যাপে অর্ডার করুন",
      lang_q: "আপনি কি ওয়েবসাইটটি বাংলায় দেখতে চান?", yes: "হ্যাঁ, বাংলা",
      no: "না, ইংরেজি", empty: "আপনার ঝুড়ি খালি আছে।", about_title: "কুণ্ডাগিফটেস সম্পর্কে",
      about_p: "আমরা বিশ্বাস করি সেরা উপহার শুধু একটি বাক্সে থাকা পণ্য নয়; এগুলো হলো ভাগ করে নেওয়া মুহূর্ত এবং দীর্ঘস্থায়ী স্মৃতি।",
      personalized: "ব্যক্তিগতকৃত", premium: "প্রিমিয়াম", personalized_sub: "প্রতিটি বক্সে হাতে লেখা নোট।",
      premium_sub: "সেরা মানের আর্টিজানাল পণ্য।", connect: "যোগাযোগ করুন",
      connect_sub: "কাস্টম অর্ডারের জন্য হোয়াটসঅ্যাপ বা ইমেলে যোগাযোগ করুন।", newsletter: "নিউজলেটার যোগ দিন",
      rights: "সর্বস্বত্ব সংরক্ষিত।", terms: "শর্তাবলী"
    }
  };

  useEffect(() => {
    const hasChosen = localStorage.getItem('langChosen');
    if (!hasChosen) setShowLangPopup(true);
    else setLang(localStorage.getItem('langChosen'));
  }, []);

  const handleLangChoice = (choice) => {
    setLang(choice);
    localStorage.setItem('langChosen', choice);
    setShowLangPopup(false);
  };

  const filteredProducts = useMemo(() => 
    filter === "All" ? PRODUCTS : PRODUCTS.filter(p => p.category === filter),
  [filter]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      return [...prev, { ...product, qty: 1 }];
    });
    setIsCartOpen(true);
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(0, item.qty + delta);
        return newQty === 0 ? null : { ...item, qty: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const total = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setCheckoutStatus('checking-out');
    const phoneNumber = "919907053292";
    let message = `*New Order from kundagiftes*%0A%0AItems List:%0A%0A`;
    cart.forEach((item, index) => {
      message += `${index + 1}. *${item.name[lang]}*%0A   Qty: ${item.qty}%0A   Price: ₹${(item.price * item.qty).toLocaleString('en-IN')}%0A%0A`;
    });
    message += `*Total Order Value: ₹${total.toLocaleString('en-IN')}*%0A%0APlease process my order!`;
    setTimeout(() => {
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
      setCheckoutStatus('success');
      setCart([]);
    }, 1500);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const socialLink = "#";

  return (
    <div className="min-h-screen bg-[#FDFCFB] text-slate-900 selection:bg-amber-100 font-sans">
      {/* Language Selection Popup */}
      {showLangPopup && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-md p-6">
          <div className="bg-white p-10 max-w-md w-full text-center rounded-2xl shadow-2xl">
            <Gift className="mx-auto text-amber-600 mb-6" size={48} />
            <h3 className="text-xl font-serif font-bold mb-8">{t.en.lang_q}</h3>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => handleLangChoice('bn')} className="bg-amber-600 text-white py-4 rounded-lg font-bold hover:bg-amber-700 transition-all">{t.bn.yes}</button>
              <button onClick={() => handleLangChoice('en')} className="bg-slate-100 text-slate-700 py-4 rounded-lg font-bold hover:bg-slate-200 transition-all">{t.en.no}</button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <nav className="fixed w-full top-0 bg-white/80 backdrop-blur-md z-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <Gift className="text-amber-600" size={28} />
            <span className="text-2xl font-serif font-bold tracking-tight text-slate-900 uppercase">KUNDAGIFTES</span>
          </div>
          <div className="hidden md:flex gap-10 font-medium text-sm tracking-widest">
            <button onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="hover:text-amber-600 transition-colors uppercase">{t[lang].home}</button>
            <button onClick={() => scrollToSection('shop')} className="hover:text-amber-600 transition-colors uppercase">{t[lang].shop}</button>
            <button onClick={() => scrollToSection('about')} className="hover:text-amber-600 transition-colors uppercase">{t[lang].about}</button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-amber-600 transition-colors uppercase">{t[lang].contact}</button>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => handleLangChoice(lang === 'en' ? 'bn' : 'en')} className="text-[10px] font-bold border border-slate-200 px-3 py-1 rounded-full uppercase hover:bg-slate-100">
              {lang === 'en' ? 'বাংলা' : 'English'}
            </button>
            <button onClick={() => setIsCartOpen(true)} className="relative p-2 hover:bg-stone-100 rounded-full">
              <ShoppingCart size={24} />
              {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">{cart.reduce((a, b) => a + b.qty, 0)}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/header.jpg" className="w-full h-full object-cover" alt="Gift Box" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <p className="uppercase tracking-[0.3em] text-sm mb-4">{t[lang].hero_sub}</p>
          <h1 className="text-5xl md:text-8xl font-serif font-bold mb-8 leading-tight">{t[lang].hero_title}</h1>
          <button onClick={() => scrollToSection('shop')} className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-4 font-bold tracking-widest transition-all">
            {t[lang].cta}
          </button>
        </div>
      </header>

      {/* Product Catalog Section */}
      <section id="shop" className="py-24 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="text-4xl font-serif font-bold mb-4 text-slate-900">{t[lang].catalog}</h2>
            <div className="flex flex-wrap gap-4">
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setFilter(cat.id)} className={`px-4 py-2 rounded-full text-xs font-bold tracking-widest transition-all ${filter === cat.id ? 'bg-amber-600 text-white shadow-lg' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}>
                  {cat[lang].toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {filteredProducts.map(product => (
            <div key={product.id} className="group cursor-pointer">
              <div className="relative aspect-[4/5] overflow-hidden mb-6 bg-stone-100">
                <MediaSlider media={product.media} name={product.name[lang]} />
                
                <button 
                  onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                  className="absolute bottom-6 left-6 right-6 z-20 bg-white py-4 font-bold text-xs tracking-[0.2em] transition-all duration-500 hover:bg-amber-600 hover:text-white transform md:translate-y-20 md:group-hover:translate-y-0 translate-y-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 shadow-lg"
                >
                  {t[lang].add} — ₹{product.price.toLocaleString('en-IN')}
                </button>
              </div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-serif text-xl font-bold text-slate-900">{product.name[lang]}</h3>
                <div className="flex items-center gap-1 text-amber-600">
                  <Star size={14} fill="currentColor" />
                  <span className="text-xs font-bold">{product.rating}</span>
                </div>
              </div>
              <p className="text-stone-500 text-sm italic">{product.desc[lang]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-stone-100 py-24 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
          <div className="md:w-1/2">
            <img src="kunda_logo.jpeg" className="w-full h-[500px] object-cover rounded-lg shadow-xl" alt="Story" />
          </div>
          <div className="md:w-1/2">
            <h2 className="text-4xl font-serif font-bold mb-6 text-slate-900">{t[lang].about_title}</h2>
            <p className="text-stone-600 leading-relaxed mb-6">{t[lang].about_p}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <Heart className="text-amber-600 mb-2" size={32} />
                <h4 className="font-bold text-slate-900">{t[lang].personalized}</h4>
                <p className="text-sm text-stone-500">{t[lang].personalized_sub}</p>
              </div>
              <div>
                <Star className="text-amber-600 mb-2" size={32} />
                <h4 className="font-bold text-slate-900">{t[lang].premium}</h4>
                <p className="text-sm text-stone-500">{t[lang].premium_sub}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 max-w-3xl mx-auto text-center">
        <h2 className="text-4xl font-serif font-bold mb-4 text-slate-900">{t[lang].connect}</h2>
        <p className="text-stone-500 mb-10">{t[lang].connect_sub}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="https://wa.me/919641573323" target="_blank" rel="noreferrer" className="bg-slate-900 text-white px-8 py-4 font-bold tracking-widest flex items-center justify-center gap-2 hover:bg-black transition-all">
            {lang === 'en' ? 'WHATSAPP US' : 'হোয়াটসঅ্যাপ করুন'} <MessageCircle size={18} />
          </a>
          <a href="mailto:nepalkundu@kundagifts.com" className="border border-slate-900 px-8 py-4 font-bold tracking-widest hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center gap-2">
            {lang === 'en' ? 'EMAIL US' : 'ইমেল করুন'} <Mail size={18} />
          </a>
        </div>
      </section>

      {/* Cart Drawer */}
      <div className={`fixed inset-0 z-[100] transition-opacity duration-500 ${isCartOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-500 transform ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-serif font-bold text-slate-900">{t[lang].cart_title}</h2>
              <button onClick={() => setIsCartOpen(false)}><X size={24} className="text-slate-500 hover:text-slate-900" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-stone-400">
                  <Package size={64} strokeWidth={1} className="mb-4" />
                  <p className="italic text-center px-6">{t[lang].empty}</p>
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} className="flex gap-4">
                    <img src={item.media[0].url} alt="" className="w-20 h-20 object-cover bg-stone-100 rounded shadow-sm" />
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-slate-900">{item.name[lang]}</h4>
                      <p className="text-amber-600 text-sm mb-2">₹{item.price.toLocaleString('en-IN')}</p>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center border rounded-full px-2">
                          <button onClick={() => updateQty(item.id, -1)} className="p-1 hover:text-amber-600"><Minus size={14} /></button>
                          <span className="w-8 text-center text-xs font-bold">{item.qty}</span>
                          <button onClick={() => updateQty(item.id, 1)} className="p-1 hover:text-amber-600"><Plus size={14} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div className="p-6 border-t bg-stone-50">
                <div className="flex justify-between mb-4 font-bold text-lg">
                  <span className="text-slate-900">{t[lang].subtotal}</span>
                  <span className="text-slate-900">₹{total.toLocaleString('en-IN')}</span>
                </div>
                <button onClick={handleCheckout} disabled={checkoutStatus === 'checking-out'} className="w-full bg-slate-900 text-white py-4 font-bold tracking-widest hover:bg-black transition-all flex items-center justify-center gap-3">
                  {t[lang].whatsapp_btn} <MessageCircle size={18} fill="currentColor" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-stone-400 py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 text-white mb-6">
              <Gift size={24} />
              <span className="text-xl font-serif font-bold tracking-widest uppercase">KUNDAGIFTES</span>
            </div>
            <p className="max-w-sm mb-8 text-sm leading-relaxed">
              {lang === 'en' ? 'Elevating the art of gifting through curated storytelling and artisanal craftsmanship.' : 'কিউরেটেড স্টোরিটেলিং এবং শৈল্পিক কারুশিল্পের মাধ্যমে উপহার দেওয়ার শিল্পকে উন্নত করা।'}
            </p>
            <div className="flex gap-4 text-white">
              <a href={socialLink} target="_blank" rel="noreferrer" className="hover:text-amber-500 transition-colors"><Instagram size={20} /></a>
              <a href={socialLink} target="_blank" rel="noreferrer" className="hover:text-amber-500 transition-colors"><Facebook size={20} /></a>
              <a href={socialLink} target="_blank" rel="noreferrer" className="hover:text-amber-500 transition-colors"><Youtube size={20} /></a>
              <a href={socialLink} target="_blank" rel="noreferrer" className="hover:text-amber-500 transition-colors"><MessageCircle size={20} /></a>
            </div>
          </div>
          <div>
            <h5 className="text-white font-bold mb-6 uppercase text-xs tracking-widest">{t[lang].shop}</h5>
            <ul className="space-y-3 text-sm">
              <li onClick={() => scrollToSection('shop')} className="hover:text-white cursor-pointer transition-colors" role="button">{t[lang].catalog}</li>
              <li onClick={() => scrollToSection('contact')} className="hover:text-white cursor-pointer transition-colors" role="button">{lang === 'en' ? 'Custom Orders Call / WP-9907053292' : 'কাস্টম অর্ডার কল / WP-9907053292 '}</li>
            </ul>
          </div>
          <div>
            <h5 className="text-white font-bold mb-6 uppercase text-xs tracking-widest">Legal</h5>
            <ul className="space-y-3 text-sm">
              <li onClick={() => setIsTermsOpen(true)} className="hover:text-white cursor-pointer transition-colors" role="button">{t[lang].terms}</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-stone-800 text-[10px] uppercase tracking-[0.3em] text-center">
          © {new Date().getFullYear()} KUNDAGIFTES. {t[lang].rights}
        </div>
      </footer>

      {/* T&C Modal Component */}
      {isTermsOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsTermsOpen(false)} />
          <div className="relative bg-white w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl p-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b">
              <h2 className="text-2xl font-serif font-bold flex items-center gap-2"><Scale className="text-amber-600" /> {t[lang].terms}</h2>
              <button onClick={() => setIsTermsOpen(false)} className="p-2 hover:bg-stone-100 rounded-full"><X /></button>
            </div>
            <div className="prose prose-sm text-stone-600 space-y-4">
              <p className="font-bold text-slate-900 italic">Welcome to KUNDAGIFTES!</p>
              <p>These terms and conditions outline the rules and regulations for the use of KUNDAGIFTES's Website.</p>
              
              <h4 className="font-bold text-slate-900 uppercase text-xs tracking-widest mt-6">1. WhatsApp Ordering Policy</h4>
              <p>KUNDAGIFTES operates as a curated gift service where final orders are processed via WhatsApp. Clicking "Order via WhatsApp" does not constitute a completed financial transaction. A contract of sale is only established once we manually confirm stock availability and shipping details with you directly via messaging.</p>

              <h4 className="font-bold text-slate-900 uppercase text-xs tracking-widest mt-6">2. Cookies</h4>
              <p>The website uses cookies to help personalize your experience. By accessing KUNDAGIFTES, you agreed to use the required cookies. These are text files placed on your hard disk by a web page server and cannot be used to run programs or deliver viruses.</p>

              <h4 className="font-bold text-slate-900 uppercase text-xs tracking-widest mt-6">3. License</h4>
              <p>Unless otherwise stated, KUNDAGIFTES owns the intellectual property rights for all material on the site. You may access this for your own personal use subjected to restrictions: You must not copy, sell, or redistribute material from KUNDAGIFTES.</p>

              <h4 className="font-bold text-slate-900 uppercase text-xs tracking-widest mt-6">4. Content Liability</h4>
              <p>We shall not be held responsible for any content that appears on external websites linking to us. No link(s) should appear on any Website that may be interpreted as libelous, obscene, or criminal.</p>

              <h4 className="font-bold text-slate-900 uppercase text-xs tracking-widest mt-6">5. Disclaimer</h4>
              <p>To the maximum extent permitted by applicable law, we exclude all representations, warranties, and conditions relating to our website. As long as the information and services on the website are provided free of charge, we will not be liable for any loss or damage of any nature.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;